import type { TFunction } from 'i18next';

/**
 * `remote_device_types` (uint8_t) from firmware
 */
export const REMOTE_DEVICE_TYPE_ENTRIES = [
  ['none', 0],
  ['drive_base', 1],
  ['drive_base_nordic', 2],
  ['drive_base_swerve', 3],
  ['controller', 4],
  ['distance_sensor', 5],
  ['beam_break_sensor', 6],
  ['motor_plus_high_speed', 7],
  ['motor_plus_high_torque', 8],
  ['magnetic_switch', 9],
  ['bee_bpr_adapter', 10],
  ['bpr_bps_software', 11],
] as const;

type Entry = (typeof REMOTE_DEVICE_TYPE_ENTRIES)[number];

export type RemoteDeviceTypeName = Entry[0];
export type RemoteDeviceTypeValue = Entry[1];

export const REMOTE_DEVICE_TYPE_VALUE_BY_NAME = Object.fromEntries(REMOTE_DEVICE_TYPE_ENTRIES) as Record<RemoteDeviceTypeName, RemoteDeviceTypeValue>;

/** Prefix for Electronic Explorer attachable electronics (sensors, motors, BPE adaptor, …). */
export const ELECTRONIC_EXPLORER_ELECTRONIC_I18N_PREFIX = 'electronicExplorer.electronic.' as const;

/** Prefix for drive base / controller / etc. entries. */
export const DRIVE_BASE_I18N_PREFIX = 'driveBase.device.type.' as const;

const REMOTE_DEVICE_TYPE_USE_ELECTRONIC_EXPLORER_PREFIX: ReadonlySet<RemoteDeviceTypeName> = new Set([
  'distance_sensor',
  'beam_break_sensor',
  'motor_plus_high_speed',
  'motor_plus_high_torque',
  'magnetic_switch',
  'bee_bpr_adapter',
]);

function remoteDeviceTypeI18nPrefix(name: RemoteDeviceTypeName): typeof ELECTRONIC_EXPLORER_ELECTRONIC_I18N_PREFIX | typeof DRIVE_BASE_I18N_PREFIX {
  return REMOTE_DEVICE_TYPE_USE_ELECTRONIC_EXPLORER_PREFIX.has(name) ? ELECTRONIC_EXPLORER_ELECTRONIC_I18N_PREFIX : DRIVE_BASE_I18N_PREFIX;
}

/**
 * Maps firmware `remote_device_types` name → Locize/i18n key segment (after we apply prefix).
 * Differs from raw kebab-case where product shortened names (motors, BPE adaptor).
 */
export const REMOTE_DEVICE_TYPE_I18N_SUFFIX_BY_NAME: Record<RemoteDeviceTypeName, string> = {
  none: 'none',
  drive_base: 'drive-base',
  drive_base_nordic: 'drive-base-nordic',
  drive_base_swerve: 'drive-base-swerve',
  controller: 'controller',
  distance_sensor: 'distance-sensor',
  beam_break_sensor: 'beam-break-sensor',
  motor_plus_high_speed: 'high-speed-motor',
  motor_plus_high_torque: 'high-torque-motor',
  magnetic_switch: 'magnetic-switch',
  bee_bpr_adapter: 'BPE-adaptor',
  bpr_bps_software: 'bpr-bps-software',
};

/** Default English strings for `t(key, defaultValue)` and display fallbacks. */
const REMOTE_DEVICE_TYPE_DISPLAY_EN: Record<RemoteDeviceTypeName, string> = {
  none: 'None',
  drive_base: 'Drive Base',
  drive_base_nordic: 'Drive Base Nordic',
  drive_base_swerve: 'Drive Base Swerve',
  controller: 'Controller',
  distance_sensor: 'Distance Sensor',
  beam_break_sensor: 'Beam Break Sensor',
  motor_plus_high_speed: 'High Speed Motor+',
  motor_plus_high_torque: 'High Torque Motor+',
  magnetic_switch: 'Magnetic Switch',
  bee_bpr_adapter: 'BPE Adaptor',
  bpr_bps_software: 'BPR BPS Software',
};

export function resolveRemoteDeviceTypeName(deviceType: string | number | null | undefined): RemoteDeviceTypeName | null {
  if (deviceType === null || deviceType === undefined) return null;

  if (typeof deviceType === 'number') {
    if (!Number.isFinite(deviceType) || Number.isNaN(deviceType)) return null;
    if (!Number.isInteger(deviceType)) return null;

    const found = REMOTE_DEVICE_TYPE_ENTRIES.find(([, value]) => value === deviceType);
    return found ? found[0] : null;
  }

  const normalized = String(deviceType).trim().replace(/\s+/g, '_');
  if (!normalized || !(normalized in REMOTE_DEVICE_TYPE_VALUE_BY_NAME)) return null;

  return normalized as RemoteDeviceTypeName;
}

/** Locize key + English default used with `t(key, defaultValue)`. Not exported separately — use {@link getElectronicExplorerElectronicI18nKey} or {@link getElectronicExplorerElectronicI18n}. */
function getRemoteDeviceTypeI18nEntry(deviceType: string | number | null | undefined): { key: string; defaultValue: string } | null {
  const name = resolveRemoteDeviceTypeName(deviceType);
  if (!name) return null;

  const suffix = REMOTE_DEVICE_TYPE_I18N_SUFFIX_BY_NAME[name];
  const prefix = remoteDeviceTypeI18nPrefix(name);

  return {
    key: `${prefix}${suffix}`,
    defaultValue: REMOTE_DEVICE_TYPE_DISPLAY_EN[name],
  };
}

/** Returns the Locize/i18n key for a known firmware `remote_device_types` value, or `''`. */
export function getElectronicExplorerElectronicI18nKey(deviceType: string | number | null | undefined): string {
  return getRemoteDeviceTypeI18nEntry(deviceType)?.key ?? '';
}

/**
 * Returns the localized display name via `t` for a known firmware type, or `null` when unmapped.
 */
export function getElectronicExplorerElectronicI18n(deviceType: string | number | null | undefined, t: TFunction): string | null {
  const entry = getRemoteDeviceTypeI18nEntry(deviceType);
  if (!entry) return null;

  return t(entry.key, entry.defaultValue);
}

/**
 * Non-localized fallback for firmware-reported strings or enum ints that are not yet in {@link REMOTE_DEVICE_TYPE_ENTRIES}.
 * Use when {@link getElectronicExplorerElectronicI18n} returns `null`.
 */
export function getRemoteDeviceTypeFallbackDisplayName(deviceType: string | number | null | undefined): string {
  if (deviceType === null || deviceType === undefined) return '';
  if (typeof deviceType === 'number') {
    if (!Number.isFinite(deviceType) || Number.isNaN(deviceType)) return '';
    if (!Number.isInteger(deviceType)) return '';

    return String(deviceType);
  }

  const trimmed = String(deviceType).trim();
  if (!trimmed) return '';

  return trimmed
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((segment) => (segment.length ? segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ');
}
